import React from "react";
import api from "@/lib/api";
import {
  X, PaperPlaneRight, Robot, User as UserIcon, Sparkle,
  WarningCircle, CheckCircle, Trash, Gear
} from "@phosphor-icons/react";
import { useErp } from "@/context/ErpContext";
import { useAuth } from "@/context/AuthContext";
import WizardFields from "@/components/erp/WizardFields";

const AGENT_KEY = "journal-create";
const BASE = "/erp-agents/journal-create";
const BRAND = "#0020C2";

const THINKING = [
  "Understanding the accounting request…",
  "Checking Oracle accounting context…",
  "Reviewing ledger and period dependencies…",
  "Preparing GL recommendations…",
  "Validating the journal draft…",
];

function Bubble({ msg, phraseIdx }) {
  const isUser = msg.role === "user";
  const cls = msg.tone === "error"
    ? "bg-red-50 border border-red-200 text-red-800"
    : msg.tone === "success"
    ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
    : "bg-white border border-gray-200 text-gray-900";

  return (
    <div className={`flex gap-2 ${isUser ? "flex-row-reverse" : ""}`}>
      <div
        className="w-6 h-6 shrink-0 flex items-center justify-center rounded-full text-white"
        style={{ background: isUser ? BRAND : msg.tone === "error" ? "#DC2626" : "#374151" }}
      >
        {isUser ? <UserIcon size={11} weight="bold" /> :
          msg.tone === "error" ? <WarningCircle size={11} weight="fill" /> :
          msg.tone === "success" ? <CheckCircle size={11} weight="fill" /> :
          <Robot size={11} weight="bold" />}
      </div>
      <div
        className={`max-w-[86%] rounded-2xl px-3 py-2 text-[13px] leading-relaxed shadow-sm ${
          isUser ? "rounded-tr-sm text-white" : `rounded-tl-sm ${cls}`
        }`}
        style={isUser ? { background: BRAND } : undefined}
      >
        {msg.loading ? (
          <div className="flex items-center gap-2 text-[11px] text-gray-400">
            <span className="flex gap-1">
              {[0, 150, 300].map((d) => (
                <span key={d} className="w-1.5 h-1.5 rounded-full animate-bounce bg-gray-400"
                  style={{ animationDelay: `${d}ms` }} />
              ))}
            </span>
            {THINKING[phraseIdx % THINKING.length]}
          </div>
        ) : (
          <div className="whitespace-pre-wrap break-words">{msg.text}</div>
        )}
      </div>
    </div>
  );
}

function HealthCard({ health }) {
  if (!health) return null;
  const valid = health.valid;
  return (
    <div className={`rounded-xl border p-3 ${valid
      ? "border-emerald-200 bg-emerald-50"
      : "border-red-200 bg-red-50"}`}>
      <div className={`flex items-center gap-2 text-[12px] font-bold ${
        valid ? "text-emerald-800" : "text-red-800"
      }`}>
        {valid ? <CheckCircle size={14} weight="fill" /> : <WarningCircle size={14} weight="fill" />}
        {valid ? "Journal is ready for staging" : "Journal needs corrections"}
      </div>
      <div className="grid grid-cols-3 gap-2 mt-2 text-[10px]">
        <div><span className="text-gray-400">Debit</span><div className="font-bold">{health.debit_total}</div></div>
        <div><span className="text-gray-400">Credit</span><div className="font-bold">{health.credit_total}</div></div>
        <div><span className="text-gray-400">Difference</span><div className="font-bold">{health.difference}</div></div>
      </div>
      {health.errors?.length > 0 && (
        <ul className="mt-2 space-y-1 text-[10px] text-red-700">
          {health.errors.map((e, i) => <li key={i}>• {e}</li>)}
        </ul>
      )}
      {health.warnings?.length > 0 && (
        <ul className="mt-2 space-y-1 text-[10px] text-amber-700">
          {health.warnings.map((e, i) => <li key={i}>• {e}</li>)}
        </ul>
      )}
    </div>
  );
}

export default function JournalAgentModal() {
  const { openAgent, setOpenAgent, setSetupOpen, profile } = useErp();
  const { user } = useAuth();

  const [sessionId] = React.useState(() =>
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `journal-${Date.now()}`
  );

  const [chatStarted, setChatStarted] = React.useState(false);
  const [messages, setMessages] = React.useState([]);
  const [input, setInput] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [phraseIdx, setPhraseIdx] = React.useState(0);

  // STATIC wizard state. The agent never controls the field list.
  const [fieldValues, setFieldValues] = React.useState({});
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [fieldWarnings, setFieldWarnings] = React.useState({});
  const [health, setHealth] = React.useState(null);
  const [draft, setDraft] = React.useState(null);
  const [staged, setStaged] = React.useState(false);
  const [success, setSuccess] = React.useState("");
  const [lastError, setLastError] = React.useState("");

  const scrollRef = React.useRef(null);

  React.useEffect(() => {
    if (!busy) return;
    const t = setInterval(() => setPhraseIdx((x) => x + 1), 1700);
    return () => clearInterval(t);
  }, [busy]);

  React.useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  if (openAgent !== AGENT_KEY) return null;

  const close = () => setOpenAgent(null);

  const normalizeForChat = (values) => {
    const out = {};
    for (const [k, v] of Object.entries(values || {})) {
      if (k.endsWith("__label")) continue;
      const label = values[`${k}__label`];
      out[k] = label ? { value: v, label } : v;
    }
    return out;
  };

  const syncWizardField = async (nextValues, previousValues) => {
    // WizardFields supplies the complete object. Find only the changed field.
    const keys = Object.keys(nextValues || {});
    const changed = keys.find((k) =>
      !k.endsWith("__label") &&
      JSON.stringify(nextValues[k]) !== JSON.stringify(previousValues?.[k])
    );
    if (!changed) return;

    try {
      await api.post(`${BASE}/set-field`, {
        session_id: sessionId,
        field: changed,
        value: nextValues[changed],
      });
    } catch (e) {
      console.warn("[journal] wizard field sync failed:", e?.response?.data?.detail || e.message);
    }
  };

  const onWizardChange = (next) => {
    const previous = fieldValues;
    setFieldValues(next);
    syncWizardField(next, previous);
    // Changing a wizard value invalidates the previous validation/stage result.
    setHealth(null);
    setStaged(false);
    setSuccess("");
  };

  const send = async (text) => {
    const userText = String(text ?? input).trim();
    if (!userText || busy) return;

    setInput("");
    setChatStarted(true);
    setLastError("");
    setSuccess("");

    setMessages((prev) => [
      ...prev,
      { id: Date.now() - 1, role: "user", text: userText },
      { id: Date.now(), role: "assistant", loading: true },
    ]);

    setBusy(true);

    try {
      const { data } = await api.post(`${BASE}/chat`, {
        session_id: sessionId,
        message: `${userText}\n\n[STATIC WIZARD CURRENT VALUES]\n${JSON.stringify(normalizeForChat(fieldValues))}`,
      });

      if (data?.status === "unconfigured") {
        setMessages((prev) => prev.map((m) =>
          m.loading ? { ...m, loading: false, text: data.response || "Please connect your Oracle Fusion profile first.", tone: "error" } : m
        ));
        setSetupOpen?.(true);
        return;
      }

      setMessages((prev) => prev.map((m) =>
        m.loading ? {
          ...m,
          loading: false,
          text: data?.response || data?.message || "Done.",
          tone: data?.status === "error" ? "error" : undefined,
        } : m
      ));

      if (data?.draft) setDraft(data.draft);
      if (data?.preflight) {
        setHealth(data.preflight);
        setFieldErrors({});
        setFieldWarnings({});
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Journal Agent request failed.";
      setLastError(detail);
      setMessages((prev) => prev.map((m) =>
        m.loading ? { ...m, loading: false, text: detail, tone: "error" } : m
      ));
    } finally {
      setBusy(false);
    }
  };

  const validate = async () => {
    setBusy(true);
    setLastError("");
    setSuccess("");
    try {
      const { data } = await api.post(`${BASE}/validate`, {
        session_id: sessionId,
        fields: fieldValues,
      });
      setHealth(data?.health || null);
      setDraft(data?.draft || null);
      setFieldErrors(data?.field_errors || {});
      setFieldWarnings(data?.field_warnings || {});
      if (data?.status === "unconfigured") {
        setLastError(data.message);
        setSetupOpen?.(true);
      }
      if (data?.message) {
        setMessages((prev) => [...prev, {
          id: Date.now(), role: "assistant", text: data.message,
          tone: data.valid ? "success" : "error"
        }]);
      }
    } catch (e) {
      setLastError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const stage = async () => {
    setBusy(true);
    setLastError("");
    try {
      const { data } = await api.post(`${BASE}/stage`, {
        session_id: sessionId,
        fields: fieldValues,
      });
      setHealth(data?.health || null);
      setDraft(data?.draft || null);
      setStaged(Boolean(data?.staged));
      if (!data?.staged) setLastError(data?.message || "Journal could not be staged.");
      else {
        setMessages((prev) => [...prev, {
          id: Date.now(),
          role: "assistant",
          text: "The journal is staged. Review the static wizard and confirm when you are ready. Nothing has been sent to Oracle yet.",
          tone: "success",
        }]);
      }
    } catch (e) {
      setLastError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!staged || busy) return;
    setBusy(true);
    setLastError("");
    try {
      const { data } = await api.post(`${BASE}/confirm`, { session_id: sessionId });
      if (data?.success) {
        setSuccess(
          data?.interface_run_id
            ? `Journal import submitted to Oracle. Interface run ID: ${data.interface_run_id}. Import submission does not by itself mean the journal is posted.`
            : data?.message || "Journal import submitted to Oracle."
        );
        setMessages((prev) => [...prev, {
          id: Date.now(),
          role: "assistant",
          text: data?.message || "Oracle accepted the JournalImportService request. The journal still has an import/validation/posting lifecycle.",
          tone: "success",
        }]);
        setStaged(false);
      } else {
        setLastError(data?.message || "Oracle rejected the journal import.");
      }
    } catch (e) {
      setLastError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4" onMouseDown={close}>
      <div
        onMouseDown={(e) => e.stopPropagation()}
        className={`bg-white rounded-2xl border border-gray-300 shadow-2xl overflow-hidden flex flex-col ${
          chatStarted ? "w-[94vw] max-w-[1450px] h-[90vh]" : "w-[760px] max-w-[calc(100vw-2rem)] max-h-[92vh]"
        }`}
      >
        <header className="shrink-0 border-b border-gray-200 px-5 py-3.5 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2]">
              Oracle Fusion General Ledger
            </div>
            <h3 className="text-xl font-black tracking-tight mt-0.5">
              Journal Creation & Allocation
            </h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              AI accounting copilot with an Oracle-backed guided wizard
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => { setOpenAgent(null); setSetupOpen?.(true); }}
              className="text-[10px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1"
            >
              <Gear size={12} /> {profile?.oracle_username || "Oracle profile"}
            </button>
            <button
              type="button"
              onClick={() => {
                setMessages([]);
                setFieldValues({});
                setHealth(null);
                setDraft(null);
                setStaged(false);
                setSuccess("");
                setLastError("");
                setChatStarted(false);
              }}
              className="text-gray-400 hover:text-gray-700"
              title="Clear"
            >
              <Trash size={15} />
            </button>
            <button type="button" onClick={close} className="text-gray-400 hover:text-gray-700">
              <X size={18} />
            </button>
          </div>
        </header>

        {lastError && (
          <div className="mx-4 mt-3 shrink-0 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-800 flex gap-2">
            <WarningCircle size={14} className="mt-0.5 shrink-0" />
            <span className="flex-1">{lastError}</span>
            <button type="button" onClick={() => setLastError("")}>×</button>
          </div>
        )}

        {success && (
          <div className="mx-4 mt-3 shrink-0 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-800 flex gap-2">
            <CheckCircle size={14} className="mt-0.5 shrink-0" />
            <span>{success}</span>
          </div>
        )}

        {!chatStarted ? (
          <>
            <div className="flex-1 overflow-y-auto min-h-0 p-4">
              <div className="mb-3 rounded-xl bg-blue-50 border border-blue-100 p-3 text-[11px] text-blue-900">
                <div className="font-bold flex items-center gap-1.5"><Sparkle size={12} /> Start with the business purpose</div>
                <div className="mt-1 text-blue-800/80">
                  The wizard is fixed like the PO agent. The AI helps explain, recommend and validate values; it does not change the wizard layout.
                </div>
              </div>
              <WizardFields
                agentKey={AGENT_KEY}
                values={fieldValues}
                onChange={onWizardChange}
                missingFields={Object.keys(fieldErrors)}
                fieldErrors={fieldErrors}
                fieldWarnings={fieldWarnings}
              />
              <div className="mt-3"><HealthCard health={health} /></div>
            </div>

            <div className="shrink-0 border-t border-gray-200 p-3">
              <div className="flex items-end gap-2">
                <textarea
                  rows={1}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                  }}
                  placeholder="Tell the Journal Agent what you want to account for…"
                  className="flex-1 resize-none px-3 py-2.5 text-[13px] border border-gray-200 rounded-xl outline-none focus:border-[#0020C2]"
                />
                <button
                  type="button"
                  disabled={busy || !input.trim()}
                  onClick={() => send()}
                  className="p-2.5 rounded-xl text-white disabled:opacity-40"
                  style={{ background: BRAND }}
                >
                  <PaperPlaneRight size={17} weight="fill" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-1 min-h-0 overflow-hidden flex-col lg:flex-row">
            {/* Agent is the primary expanded interface */}
            <section className="flex-1 min-w-0 min-h-0 flex flex-col border-b lg:border-b-0 lg:border-r border-gray-200">
              <div className="shrink-0 border-b border-gray-200 bg-white px-4 py-2.5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-gray-800 text-white flex items-center justify-center">
                    <Robot size={12} />
                  </div>
                  <div>
                    <div className="text-[11px] font-bold">Journal Agent</div>
                    <div className="text-[9px] text-gray-400">Primary interface</div>
                  </div>
                </div>
                <span className="text-[9px] text-gray-400">{busy ? "Working…" : "Ready"}</span>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto bg-gray-50 p-4 space-y-2.5">
                {messages.map((msg) => <Bubble key={msg.id} msg={msg} phraseIdx={phraseIdx} />)}

                {draft && (
                  <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-[10px] text-blue-900">
                    <div className="font-bold">Current draft context</div>
                    <div className="mt-1">
                      The wizard on the right is the authoritative structured input. The agent may recommend changes, but will not overwrite user selections.
                    </div>
                  </div>
                )}

                {staged && (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                    <div className="text-[12px] font-bold text-emerald-900 flex items-center gap-2">
                      <CheckCircle size={14} weight="fill" /> Ready for explicit confirmation
                    </div>
                    <div className="text-[10px] text-emerald-800 mt-1">
                      Oracle has not received the journal yet.
                    </div>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={confirm}
                      className="mt-2 rounded-lg bg-emerald-600 px-3 py-2 text-[11px] font-bold text-white disabled:opacity-50"
                    >
                      Confirm & Create
                    </button>
                  </div>
                )}
              </div>

              <div className="shrink-0 border-t border-gray-200 bg-white p-3">
                <div className="flex items-end gap-2">
                  <textarea
                    rows={1}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                    }}
                    placeholder="Ask about the journal, a field, ledger, period or GL recommendation…"
                    className="flex-1 resize-none px-3 py-2.5 text-[13px] border border-gray-200 rounded-xl outline-none focus:border-[#0020C2]"
                  />
                  <button
                    type="button"
                    disabled={busy || !input.trim()}
                    onClick={() => send()}
                    className="p-2.5 rounded-xl text-white disabled:opacity-40"
                    style={{ background: BRAND }}
                  >
                    <PaperPlaneRight size={17} weight="fill" />
                  </button>
                </div>
              </div>
            </section>

            {/* Static wizard — independent of agent prompting */}
            <aside className="lg:w-[520px] xl:w-[560px] shrink-0 min-h-0 bg-white flex flex-col">
              <div className="shrink-0 border-b border-gray-200 px-4 py-2.5">
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-400">
                  Guided wizard
                </div>
                <div className="text-[9px] text-gray-400 mt-0.5">
                  Fixed schema · Oracle-backed controls · editable recommendations
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                <WizardFields
                  agentKey={AGENT_KEY}
                  values={fieldValues}
                  onChange={onWizardChange}
                  missingFields={Object.keys(fieldErrors)}
                  fieldErrors={fieldErrors}
                  fieldWarnings={fieldWarnings}
                />
                <div className="mt-3"><HealthCard health={health} /></div>
              </div>

              <div className="shrink-0 border-t border-gray-200 p-3 bg-gray-50 flex gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={validate}
                  className="flex-1 rounded-lg border border-[#0020C2] text-[#0020C2] px-3 py-2.5 text-[10px] font-bold uppercase tracking-wider disabled:opacity-40"
                >
                  Validate Draft
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={stage}
                  className="flex-1 rounded-lg bg-[#0020C2] text-white px-3 py-2.5 text-[10px] font-bold uppercase tracking-wider disabled:opacity-40"
                >
                  Stage for Confirmation
                </button>
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}